/* Pure calendar helpers. All arithmetic is done on UTC calendar dates so results
 * never depend on the host time zone (KSA has no DST; we mirror that in Go). */
import type { ISODate } from "../data/types";

const DAY = 86400000;
const re = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isISODate(s: unknown): s is ISODate {
  if (typeof s !== "string" || !re.test(s)) return false;
  const [y, m, d] = s.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

export function toUTC(s: ISODate): number {
  if (!isISODate(s)) throw new Error("invalid date: " + s);
  const [y, m, d] = s.split("-").map(Number);
  return Date.UTC(y, m - 1, d);
}

export function fromUTC(ms: number): ISODate {
  const d = new Date(ms);
  return d.getUTCFullYear() + "-" + String(d.getUTCMonth() + 1).padStart(2, "0") + "-" + String(d.getUTCDate()).padStart(2, "0");
}

/** Whole calendar days from `a` to `b` (b - a). */
export function daysBetween(a: ISODate, b: ISODate): number {
  return Math.round((toUTC(b) - toUTC(a)) / DAY);
}

export function addDays(d: ISODate, n: number): ISODate {
  return fromUTC(toUTC(d) + n * DAY);
}

/** Days remaining from today until `d` (negative = overdue). */
export function daysTo(today: ISODate, d: ISODate): number {
  return daysBetween(today, d);
}

/** Service years as a decimal: days / 365.2425 (never negative). */
export function yearsBetween(from: ISODate, to: ISODate): number {
  return Math.max(0, daysBetween(from, to) / 365.2425);
}

/** 0=Sunday … 6=Saturday */
export function weekday(d: ISODate): number {
  return new Date(toUTC(d)).getUTCDay();
}

/** Inclusive day count between two dates (≥ 0 when to ≥ from, else 0). */
export function inclusiveDays(from: ISODate, to: ISODate): number {
  const n = daysBetween(from, to) + 1;
  return n > 0 ? n : 0;
}

/** Last calendar day of the month of `d`. */
export function monthEnd(d: ISODate): ISODate {
  const [y, m] = d.split("-").map(Number);
  return fromUTC(Date.UTC(y, m, 0));
}

/** "HH:mm" → minutes since midnight; throws on bad input. */
export function hmToMin(hm: string): number {
  const m = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(hm);
  if (!m) throw new Error("invalid time: " + hm);
  const h = +m[1], mi = +m[2];
  if (h > 23 || mi > 59) throw new Error("invalid time: " + hm);
  return h * 60 + mi;
}

export function minToHm(min: number): string {
  const m = ((Math.round(min) % 1440) + 1440) % 1440;
  return String(Math.floor(m / 60)).padStart(2, "0") + ":" + String(m % 60).padStart(2, "0");
}
